import { Controller } from "@hotwired/stimulus"

// Toolbar/page-level glue for the Membres index. Same shape as
// adherents_index_controller / contacts_index_controller — exposes
// window.__listIndex so sibling controllers can drive it.
export default class extends Controller {
  static targets = ["searchInput"]

  static values = {
    config:               Object,
    currentView:          String,
    filters:              Object,
    savedViewFilterKeys:  Array,
    viewType:             String
  }

  connect() {
    window.__membresIndex = this
    window.__listIndex    = this
  }

  applySort(event) {
    const t = event.currentTarget
    const field = t.dataset.sortField
    const direction = t.dataset.sortDirection || "asc"
    if (!field) return
    this.navigateWith({ sort_field: field, sort_direction: direction })
  }

  submitSearch(event) {
    event?.preventDefault?.()
    const value = this.hasSearchInputTarget ? this.searchInputTarget.value.trim() : ""
    const params = this._currentParams()
    Object.keys(params).forEach(p => { if (p.startsWith("filters[search]")) delete params[p] })
    if (value) {
      params["filters[search][op]"] = "contains"
      params["filters[search][value]"] = value
    } else if (/^\d+$/.test(this.currentViewValue || "")) {
      params["filters[search][op]"] = "__none__"
    }
    delete params["page"]
    this._navigate(params)
  }

  clearSearch() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    this.submitSearch()
  }

  removeFilter(event) {
    const key = event.currentTarget.dataset.filterKey
    if (!key) return
    const params = this._currentParams()
    Object.keys(params).forEach((p) => { if (p.startsWith(`filters[${key}]`)) delete params[p] })

    const onSavedView = /^\d+$/.test(this.currentViewValue || "")
    if (onSavedView) params[`filters[${key}][op]`] = "__none__"
    delete params["page"]
    this._navigate(params)
  }

  navigateWith(extra) {
    const params = this._currentParams()
    Object.entries(extra || {}).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "") delete params[k]
      else params[k] = v
    })
    this._navigate(params)
  }

  applyFilters(filters) {
    const params = this._currentParams()
    Object.keys(params).forEach((k) => { if (k.startsWith("filters[")) delete params[k] })

    const onSavedView    = /^\d+$/.test(this.currentViewValue || "")
    const effectiveKeys  = Object.keys(this.filtersValue || {})
    const intrinsicKeys  = Array.isArray(this.savedViewFilterKeysValue) ? this.savedViewFilterKeysValue : []
    const previousActive = Array.from(new Set([...effectiveKeys, ...intrinsicKeys]))
    const newActive      = []

    Object.entries(filters || {}).forEach(([key, val]) => {
      if (!val || typeof val !== "object") return
      const op = val.op
      if (!op) return

      let written = false
      switch (op) {
        case "in": {
          const values = (val.value || []).filter(v => v !== null && v !== undefined && v !== "")
          if (!values.length) return
          params[`filters[${key}][value][]`] = values
          params[`filters[${key}][op]`] = "in"
          written = true
          break
        }
        case "between":
          if (val.from) params[`filters[${key}][from]`] = val.from
          if (val.to)   params[`filters[${key}][to]`]   = val.to
          if (val.from || val.to) {
            params[`filters[${key}][op]`] = "between"
            written = true
          }
          break
        case "eq":
        case "gte":
        case "lte":
        case "contains":
          if (val.value === null || val.value === undefined || val.value === "") return
          params[`filters[${key}][value]`] = val.value
          params[`filters[${key}][op]`] = op
          written = true
          break
      }
      if (written) newActive.push(key)
    })

    if (onSavedView) {
      previousActive.forEach(key => {
        if (!newActive.includes(key)) params[`filters[${key}][op]`] = "__none__"
      })
    }

    this._navigate(params)
  }

  _currentParams() {
    const url = new URL(window.location.href)
    const out = {}
    url.searchParams.forEach((value, key) => {
      if (key.endsWith("[]")) {
        if (!out[key]) out[key] = []
        out[key].push(value)
      } else {
        out[key] = value
      }
    })
    return out
  }

  _navigate(params) {
    const url = new URL(window.location.href)
    url.search = ""
    Object.entries(params).forEach(([key, val]) => {
      if (val === null || val === undefined) return
      if (Array.isArray(val)) val.forEach((v) => url.searchParams.append(key, v))
      else url.searchParams.set(key, val)
    })
    window.location.href = url.toString()
  }
}
